import { Box, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import type { ContentItem } from '../../../models/ContentManagementInterface';
import type BackendService from '../../../services/admin/BackendService';
import DocumentLibraryCard from './DocumentLibraryCard';
import DocumentViewerDialog from './DocumentViewerDialog';
import { DocumentsPdfDropZone } from './MediaLibraryDocumentParts';
import { useDocumentLibrary } from './useDocumentLibrary';
import type { ImageCardsCropMeta } from './useImageCardsSplit';
import type { ImportedPageRange } from './documentImportLimits';

interface UploadOnlyDocumentPickerProps {
  fundOpportunityId: string;
  backendService: BackendService;
  modelType: string;
  modelId: string;
  onImported: (
    sections: ContentItem[],
    cropMeta?: ImageCardsCropMeta | null,
  ) => void | Promise<void>;
  /** A page-limited batch of the uploaded document was imported (pages may remain). */
  onBatchImported?: (documentId: string, range: ImportedPageRange) => void;
  /** Closes the upload flow; the caller opens the saved draft if any pages were imported. */
  onCancel: () => void;
}

/**
 * A pared-down version of the Documents tab for adding a single new document and importing
 * from it right away — no search field, no list of everything already in the fund's library
 * (that's what the Documents tab itself is for). Drops a file, then opens its page/range/region
 * picker automatically once it's ready (immediately for a PDF; after background conversion
 * finishes for a Word/PowerPoint file).
 */
export default function UploadOnlyDocumentPicker({
  fundOpportunityId,
  backendService,
  modelType,
  modelId,
  onImported,
  onBatchImported,
  onCancel,
}: UploadOnlyDocumentPickerProps) {
  const library = useDocumentLibrary({ fundOpportunityId, backendService });
  const [uploadedId, setUploadedId] = useState<string | null>(null);
  const [viewing, setViewing] = useState(false);
  const uploadedDoc = library.documents.find((doc) => doc.externalId === uploadedId) ?? null;

  useEffect(() => {
    if (uploadedDoc?.status === 'READY') setViewing(true);
  }, [uploadedDoc?.status]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="body2" color="text.secondary">
        Upload a document to choose a page, a page range, or part of a page.
      </Typography>
      <DocumentsPdfDropZone
        uploading={library.uploading}
        onUpload={async (file, title, description) => {
          const created = await library.upload(file, title, description);
          if (created) {
            setUploadedId(created.externalId);
            setViewing(false);
          }
        }}
      />
      {library.uploadError && (
        <Typography variant="caption" color="error.main">
          {library.uploadError}
        </Typography>
      )}
      {uploadedDoc && !viewing && (
        <DocumentLibraryCard
          doc={uploadedDoc}
          fluid
          onImport={uploadedDoc.status === 'READY' ? () => setViewing(true) : undefined}
        />
      )}
      <DocumentViewerDialog
        key={uploadedId ?? ''}
        open={viewing}
        documentAsset={uploadedDoc}
        pickTarget={null}
        modelType={modelType}
        modelId={modelId}
        backendService={backendService}
        autoAdvance
        onImported={onImported}
        onBatchImported={(range) => {
          if (uploadedId) onBatchImported?.(uploadedId, range);
        }}
        onClose={onCancel}
      />
    </Box>
  );
}
